import { getActionableDescriptionQualityReasons } from 'partner360/entities/keyInitiatives'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { formatMoney } from 'partner360/features/keyInitiatives/ui/helpers/formatMoney'
import { getDescriptionQualityReasonTranslation } from 'partner360/features/keyInitiatives/ui/helpers/getDescriptionQualityReasonTranslation'

import type {
  ICockpitExportCell,
  ICockpitExportPillar,
  ICockpitExportPillarBreakdown,
  ICockpitExportTable,
} from '../../../../api'
import type { TCockpitInitiativesQuality } from '../../../../lib'
import { getPillarScore, toExportText } from '../../../components/cockpitExport'

const KEYS = TRANSLATION_KEYS.quality
const STRATEGIC_KEYS = KEYS.strategicAlignment

type TTranslate = (key: string) => string

/** Which of the cards' panels the user has open — each panel is exported only while open. */
export interface IInitiativesQualityOpenPanels {
  financialPlanCoverageTable: boolean
  descriptionQualityTable: boolean
  strategicBreakdown: boolean
  strategicTable: boolean
}

interface IDetailsColumn<T> {
  key: string
  /** An i18n key, as the column's own header shows it. */
  title: string
  isNumeric?: boolean
  cell: (item: T) => ICockpitExportCell
}

/** A card's details table: every row, in the order the card lists them. */
const toTable = <T extends { EntryId: number }>(
  items: T[],
  columns: IDetailsColumn<T>[],
  translate: TTranslate
): ICockpitExportTable => ({
  columns: columns.map(({ key, title, isNumeric }) => ({
    key,
    title: translate(title),
    isNumeric,
  })),
  rows: items.map((item) => ({
    id: String(item.EntryId),
    cells: Object.fromEntries(columns.map(({ key, cell }) => [key, cell(item)])),
  })),
})

/** The Strategic growth areas breakdown, as `StrategicAlignmentPillar` lists it once opened:
 *  50% per counted initiative, capped at 100%. */
const getStrategicBreakdown = (
  strategic: TCockpitInitiativesQuality['strategicGrowthAreas'],
  translate: TTranslate
): ICockpitExportPillarBreakdown => {
  const mdfCount = strategic.mdfInitiatives.length
  const priorityCount = strategic.initiativesWithBusinessPrioritiesCount
  const rawScore = (mdfCount + priorityCount) * 50
  const overlapCount = strategic.mdfInitiatives.filter(({ BusinessPriorities }) =>
    BusinessPriorities?.split(',').some((uid) => uid.trim())
  ).length

  return {
    lines: [
      ...[
        { title: STRATEGIC_KEYS.mdfGroup, count: mdfCount },
        { title: STRATEGIC_KEYS.priorityGroup, count: priorityCount },
      ].map(({ title, count }) => ({
        label: toExportText(translate(title)),
        formula: `${count} × 50%`,
        value: `${count * 50}%`,
      })),
      {
        label: toExportText(translate(STRATEGIC_KEYS.scoreLabel)),
        value: `${strategic.score}%`,
        isTotal: true,
        tag:
          rawScore > 100
            ? toExportText(translate(STRATEGIC_KEYS.cappedScore), [
                { key: '{score}', value: String(rawScore) },
              ])
            : undefined,
      },
    ],
    note:
      overlapCount > 0
        ? toExportText(
            translate(overlapCount === 1 ? STRATEGIC_KEYS.overlapOne : STRATEGIC_KEYS.overlapMany),
            [{ key: '{count}', value: String(overlapCount) }]
          )
        : undefined,
  }
}

interface IParams {
  quality: TCockpitInitiativesQuality
  currency: string
  open: IInitiativesQualityOpenPanels
  translate: TTranslate
}

/**
 * The three pillar cards of a single partner's Initiatives Plan Quality, as
 * `CockpitInitiativesQuality` draws them: the texts, params and conditions are the ones it hands
 * `QualityPillar` and `StrategicAlignmentPillar`, so keep the two in step.
 */
export const getInitiativesQualityPillarsExport = ({
  quality,
  currency,
  open,
  translate,
}: IParams): ICockpitExportPillar[] => {
  const {
    financialPlanCoverage: coverage,
    descriptionQuality: description,
    strategicGrowthAreas: strategic,
  } = quality
  const nameCell = ({ Name }: { Name: string | null }): ICockpitExportCell => ({ text: Name || '' })

  return [
    {
      key: 'financialPlanCoverage',
      title: translate(KEYS.financialPlanCoverage.title),
      ...getPillarScore(coverage.score, coverage.band, translate),
      /** A pillar with nothing to score reads why in place of its description. */
      description:
        coverage.score === null
          ? toExportText(translate(KEYS.financialPlanCoverage.notApplicable))
          : toExportText(translate(KEYS.financialPlanCoverage.description), [
              { key: '{goalCoverage}', value: String(coverage.goalCoverage) },
              { key: '{incrementalCoverage}', value: String(coverage.incrementalCoverage) },
              {
                key: '{IncrementalPlanSum}',
                value: formatMoney(coverage.incrementalPlanSum, currency),
              },
              {
                key: '{IncrementalInitiativesSum}',
                value: formatMoney(coverage.incrementalInitiativesSum, currency),
              },
            ]),
      table: open.financialPlanCoverageTable
        ? toTable(
            coverage.initiativesWithIncremental,
            [
              { key: 'name', title: KEYS.details.name, cell: nameCell },
              {
                key: 'incremental',
                title: KEYS.details.incremental,
                isNumeric: true,
                cell: ({ incremental }) => ({
                  text: formatMoney(incremental, currency),
                  value: incremental,
                }),
              },
            ],
            translate
          )
        : undefined,
    },
    {
      key: 'descriptionQuality',
      title: translate(KEYS.descriptionQuality.title),
      ...getPillarScore(description.score, description.band, translate),
      description: toExportText(translate(KEYS.descriptionQuality.description), [
        { key: '{wellDescribedCount}', value: String(description.wellDescribedCount) },
        { key: '{totalCount}', value: String(description.totalCount) },
      ]),
      note:
        description.lowScoredCount === 0
          ? toExportText(translate(KEYS.descriptionQuality.success))
          : undefined,
      table: open.descriptionQualityTable
        ? toTable(
            description.lowScoredInitiatives,
            [
              { key: 'name', title: KEYS.details.name, cell: nameCell },
              {
                key: 'description',
                title: KEYS.details.description,
                cell: ({ Description }) => ({ text: Description || '' }),
              },
              {
                key: 'suggestions',
                title: KEYS.details.suggestions,
                cell: ({ reasons }) => ({
                  text: getActionableDescriptionQualityReasons(reasons)
                    .map((reason) =>
                      translate(getDescriptionQualityReasonTranslation(reason, true))
                    )
                    .join(', '),
                }),
              },
            ],
            translate
          )
        : undefined,
    },
    {
      key: 'strategicGrowthAreas',
      title: translate(STRATEGIC_KEYS.title),
      ...getPillarScore(strategic.score, strategic.band, translate),
      description: toExportText(translate(STRATEGIC_KEYS.groupSummary), [
        { key: '{mdfCount}', value: String(strategic.mdfInitiatives.length) },
        {
          key: '{priorityCount}',
          value: String(strategic.initiativesWithBusinessPrioritiesCount),
        },
      ]),
      /** With every initiative tied to a priority, the card says so where its button would be. */
      note:
        strategic.initiativesWithoutBusinessPriorities.length === 0 &&
        strategic.mdfInitiatives.length + strategic.initiativesWithBusinessPrioritiesCount > 0
          ? toExportText(translate(STRATEGIC_KEYS.allHavePriority))
          : undefined,
      breakdown: open.strategicBreakdown ? getStrategicBreakdown(strategic, translate) : undefined,
      table: open.strategicTable
        ? toTable(
            strategic.initiativesWithoutBusinessPriorities,
            [
              { key: 'name', title: KEYS.details.name, cell: nameCell },
              {
                key: 'strategicInitiative',
                title: TRANSLATION_KEYS.dashboard.table.columns.strategicInitiative,
                cell: ({ Development }) => ({ text: translate(Development || '') }),
              },
              {
                key: 'description',
                title: KEYS.details.description,
                cell: ({ Description }) => ({ text: Description || '' }),
              },
            ],
            translate
          )
        : undefined,
    },
  ]
}
